<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { api } from "../lib/api";
  import type { MetricsSnapshot } from "@antares/shared";

  interface Props {
    ns: string;
    name: string;
  }

  let { ns, name }: Props = $props();

  let cpuWrap: HTMLDivElement;
  let memWrap: HTMLDivElement;
  let cpuChart: any;
  let memChart: any;
  let uPlotLib: any;
  let interval: ReturnType<typeof setInterval>;
  let resizeObserver: ResizeObserver;
  let chartsReady = false;

  let history = $state<MetricsSnapshot[]>([]);
  let latest = $state<MetricsSnapshot | null>(null);
  let errorMsg = $state("");

  onMount(async () => {
    uPlotLib = (await import("uplot")).default;
    await import("uplot/dist/uPlot.min.css");

    // Watch for the container becoming visible / resizing
    resizeObserver = new ResizeObserver(() => {
      if (!chartsReady && cpuWrap.clientWidth > 0) {
        initCharts();
      } else if (chartsReady) {
        cpuChart?.setSize({ width: cpuWrap.clientWidth, height: 200 });
        memChart?.setSize({ width: memWrap.clientWidth, height: 200 });
      }
    });
    resizeObserver.observe(cpuWrap);

    // If already visible (metrics is the active tab), init now
    if (cpuWrap.clientWidth > 0) {
      initCharts();
    }

    // Start polling regardless — data accumulates even before charts init
    await fetchMetrics();
    interval = setInterval(fetchMetrics, 10_000);
  });

  onDestroy(() => {
    clearInterval(interval);
    resizeObserver?.disconnect();
    cpuChart?.destroy();
    memChart?.destroy();
  });

  function initCharts() {
    if (chartsReady || !uPlotLib) return;

    const width = cpuWrap.clientWidth;
    const now = Date.now() / 1000;

    cpuChart = new uPlotLib(
      makeOpts("CPU (millicores)", "mCPU", width),
      [history.length ? history.map(ts) : [now], history.length ? history.map(s => s.cpu.usageMilliCores) : [0]],
      cpuWrap
    );

    memChart = new uPlotLib(
      makeOpts("Memory (MiB)", "MiB", width),
      [history.length ? history.map(ts) : [now], history.length ? history.map(s => s.memory.usageBytes / (1024 * 1024)) : [0]],
      memWrap
    );

    chartsReady = true;
  }

  function makeOpts(title: string, yLabel: string, width: number) {
    return {
      width,
      height: 200,
      title,
      cursor: { show: true },
      series: [
        {},
        {
          label: yLabel,
          stroke: "#6c8cff",
          fill: "rgba(108, 140, 255, 0.1)",
          width: 2,
        },
      ],
      axes: [
        {
          stroke: "#8b90a0",
          grid: { stroke: "rgba(46, 51, 70, 0.5)", width: 1 },
          ticks: { stroke: "rgba(46, 51, 70, 0.5)", width: 1 },
          font: "11px sans-serif",
        },
        {
          stroke: "#8b90a0",
          grid: { stroke: "rgba(46, 51, 70, 0.5)", width: 1 },
          ticks: { stroke: "rgba(46, 51, 70, 0.5)", width: 1 },
          font: "11px sans-serif",
        },
      ],
      scales: {
        x: { time: true },
      },
    };
  }

  function ts(s: MetricsSnapshot): number {
    return new Date(s.timestamp).getTime() / 1000;
  }

  async function fetchMetrics() {
    try {
      const snap = await api.getMetrics(ns, name);
      latest = snap;
      history = [...history.slice(-59), snap];
      updateCharts();
    } catch (err: any) {
      errorMsg = err.message;
    }
  }

  function updateCharts() {
    if (!chartsReady || history.length === 0) return;

    const times = history.map(ts);
    const cpuVals = history.map((s) => s.cpu.usageMilliCores);
    const memVals = history.map((s) => s.memory.usageBytes / (1024 * 1024));

    cpuChart.setData([times, cpuVals]);
    memChart.setData([times, memVals]);
  }

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KiB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
  }
</script>

<div class="metrics-panel">
  {#if errorMsg}
    <div class="error">{errorMsg}</div>
  {/if}

  {#if latest}
    <div class="stats-row">
      <div class="stat">
        <span class="stat-label">CPU</span>
        <span class="stat-value">{latest.cpu.usageMilliCores}m</span>
      </div>
      <div class="stat">
        <span class="stat-label">Memory</span>
        <span class="stat-value">{formatBytes(latest.memory.usageBytes)}</span>
      </div>
    </div>
  {/if}

  <div class="chart-container" bind:this={cpuWrap}></div>
  <div class="chart-container" bind:this={memWrap}></div>
</div>

<style>
  .metrics-panel {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .error {
    color: var(--danger);
    font-size: 0.85rem;
  }

  .stats-row {
    display: flex;
    gap: 2rem;
  }

  .stat {
    display: flex;
    flex-direction: column;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 0.75rem 1rem;
    min-width: 120px;
  }

  .stat-label {
    font-size: 0.75rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .stat-value {
    font-size: 1.25rem;
    font-weight: 600;
    font-family: var(--font-mono);
    margin-top: 0.25rem;
  }

  .chart-container {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 0.5rem;
    overflow: hidden;
    min-height: 220px;
  }

  :global(.uplot) {
    font-family: var(--font-sans);
  }

  :global(.u-title) {
    font-size: 0.8rem !important;
    color: var(--text-muted) !important;
  }

  :global(.u-legend) {
    font-size: 0.75rem !important;
  }

  :global(.u-legend .u-series td) {
    color: var(--text-muted) !important;
  }
</style>
